import type { OpcionesCliente } from '../api/cliente';
import type { Aviso, EstadoSuscripcion, ListaAvisos, ParDeTokens, PreferenciasMenu, ResumenAvisos, RetoDosPasos } from '../api/operaciones';
import type { Problema } from '../api/problema';
import {
    CUENTA_DEMO,
    NEGOCIOS,
    SIN_PREFERENCIAS,
    accesoSimulado,
    avisos,
    bloques,
    estadoDe,
    listasPequenas,
    modoDe,
    navegacion,
    negocioSimulado,
    permisosDelNegocio,
    preferenciasNoValidas,
    suscripcion,
    tiposDeAviso,
    unirPreferencias,
    uso,
    yo,
} from './datos';

/**
 * Capa de simulación del Paso 0 (`VITE_SIMULACION=1`). Sustituye a la red **dentro** del cliente de la API: el panel
 * sigue llamando a las mismas operaciones /v1 y recibe respuestas y `Problem` con la forma exacta del contrato. Al
 * llegar el módulo Identidad y acceso se apaga en la configuración y, después, se borra esta carpeta.
 *
 * Reglas del prototipo (diseno/plataforma/1.1-1.6-1.10): contraseña «error» → no coinciden; código «000000» → código
 * incorrecto; la cuenta tiene los dos pasos activados y trabaja en tres negocios.
 */
export interface OpcionesSimulacion {
    /** Negocios en solo lectura (pausa de temporada). */
    soloLectura?: boolean;
    /** La cuenta solo trabaja en un negocio: se entra sin elegir. */
    unNegocio?: boolean;
    /** La cuenta no trabaja en ningún negocio (1.3, «sin negocio», G122). */
    sinNegocio?: boolean;
    /** La cuenta no tiene los dos pasos: createSession devuelve ya la sesión. */
    sinDosPasos?: boolean;
    /** Cuenta de Soporte sin los dos pasos (G65): entrar en un negocio responde `403 two_factor_required`. */
    soporteSinDosPasos?: boolean;
    /** Estado de la suscripción del negocio en el que se entra (0.5), en lugar del del prototipo. */
    suscripcion?: EstadoSuscripcion | null;
    /** Cupo de conversaciones al 90 % (0.5). */
    cupo?: boolean;
    /** Cupo de conversaciones al 100 % (0.5, G777): franja roja sin cerrar. */
    cupo100?: boolean;
    /**
     * Acceso de plataforma abierto en Caseta Embrujo (G137): quien entra es Soporte en solo lectura (G70) o suplantando
     * (0.5), o el negocio ve que Soporte lo está consultando (`negocio`, franja negra).
     */
    acceso?: 'soporte' | 'suplantacion' | 'negocio' | null;
    /** Milisegundos de espera de cada respuesta, para ver los estados de carga. */
    espera?: number;
    ahora?: () => Date;
}

type FetchCliente = NonNullable<OpcionesCliente['fetch']>;

const RETO = 'reto-simulado';

function json(estado: number, cuerpo: unknown): Response {
    return new Response(JSON.stringify(cuerpo), { status: estado, headers: { 'Content-Type': 'application/json' } });
}

function problema(estado: number, code: Problema['code'], title: string, extra: Partial<Problema> = {}): Response {
    const cuerpo: Problema = { type: `https://api.reservaria.app/errors/${code}`, title, status: estado, code, correlation_id: '7F3A-91C2', ...extra };
    return new Response(JSON.stringify(cuerpo), { status: estado, headers: { 'Content-Type': 'application/problem+json' } });
}

function tokens(negocio: string | null): ParDeTokens {
    const sufijo = negocio ?? 'sin-negocio';
    return { access_token: `acceso-simulado.${sufijo}`, refresh_token: `refresco-simulado.${sufijo}`, expires_in: 900, business_id: negocio };
}

function cuerpoDe(init: RequestInit | undefined): Record<string, unknown> {
    if (typeof init?.body !== 'string') {
        return {};
    }
    const valor: unknown = JSON.parse(init.body);
    return typeof valor === 'object' && valor !== null ? (valor as Record<string, unknown>) : {};
}

function cabecera(init: RequestInit | undefined, nombre: string): string | null {
    const cabeceras = init?.headers;
    if (cabeceras === undefined || cabeceras instanceof Headers || Array.isArray(cabeceras)) {
        return null;
    }
    return cabeceras[nombre] ?? null;
}

export function crearFetchSimulado(opciones: OpcionesSimulacion = {}): FetchCliente {
    const ahora = opciones.ahora ?? (() => new Date());
    const negociosValidos: string[] = Object.values(NEGOCIOS);
    const bandeja = new Map<string, Aviso[]>(negociosValidos.map((id) => [id, avisos(ahora())]));

    const silencios = new Map<string, ReturnType<typeof tiposDeAviso>>();
    function avisosDe(negocio: string): Aviso[] {
        return bandeja.get(negocio) ?? [];
    }

    function resumen(negocio: string): ResumenAvisos {
        const sinLeer = avisosDe(negocio).filter((a) => a.state === 'unread');
        return {
            unread: sinLeer.length,
            by_severity: {
                critical: sinLeer.filter((a) => a.severity === 'critical').length,
                warning: sinLeer.filter((a) => a.severity === 'warning').length,
                info: sinLeer.filter((a) => a.severity === 'info').length,
            },
        };
    }

    function cambiarAviso(negocio: string, id: string, cambio: (aviso: Aviso) => Aviso): Response {
        const lista = avisosDe(negocio);
        const aviso = lista.find((a) => a.id === id);
        if (aviso === undefined) {
            return problema(404, 'not_found', 'Aviso no encontrado');
        }
        const nuevo = cambio(aviso);
        bandeja.set(negocio, lista.map((a) => (a.id === id ? nuevo : a)));
        return json(200, nuevo);
    }

    const marca = () => ahora().toISOString().replace(/\.\d{3}Z$/, 'Z');
    // Preferencias del menú guardadas por negocio (G129) y el acceso de plataforma, hasta que se cierre con «Salir».
    const preferencias = new Map<string, PreferenciasMenu>();
    let accesoAbierto = opciones.acceso ?? null;
    const accesoEn = (negocio: string) => (accesoAbierto !== null && negocio === NEGOCIOS.embrujo ? accesoSimulado(accesoAbierto, ahora()) : null);
    // El estado forzado (`?suscripcion=`) vale para el negocio de la sesión, que va en el token simulado.
    let negocioDeLaSesion: string | null = null;
    const estado = (negocio: string) =>
        estadoDe(negocio, { soloLectura: opciones.soloLectura === true, suscripcion: opciones.suscripcion ?? null, negocioForzado: negocioDeLaSesion });

    function responder(metodo: string, url: URL, init: RequestInit | undefined): Response {
        const ruta = url.pathname;
        const cuerpo = cuerpoDe(init);
        const token = cabecera(init, 'Authorization');
        const delToken = token === null ? null : /\.(biz_[0-9A-Z]+)$/.exec(token);
        negocioDeLaSesion = delToken === null ? null : (delToken[1] ?? null);

        // ---- Sesión ----
        if (metodo === 'POST' && ruta === '/v1/auth/sessions') {
            if (cuerpo.password === CUENTA_DEMO.contrasenaErronea) {
                return problema(401, 'unauthenticated', 'El email o la contraseña no coinciden');
            }
            const reto: RetoDosPasos = { two_factor_required: true, challenge_token: RETO };
            return json(200, opciones.sinDosPasos === true ? tokens(null) : reto);
        }
        if (metodo === 'POST' && ruta === '/v1/auth/sessions/two-factor') {
            if (cuerpo.challenge_token !== RETO) {
                return problema(401, 'unauthenticated', 'El reto ha caducado');
            }
            if (cuerpo.code === CUENTA_DEMO.codigoErroneo || cuerpo.recovery_code === CUENTA_DEMO.codigoErroneo) {
                return problema(422, 'code_invalid', 'El código no es correcto');
            }
            return json(200, tokens(null));
        }
        if (metodo === 'POST' && ruta === '/v1/auth/sessions/refresh') {
            const refresco = typeof cuerpo.refresh_token === 'string' ? /^refresco-simulado\.(.+)$/.exec(cuerpo.refresh_token) : null;
            if (refresco === null) {
                return problema(401, 'unauthenticated', 'La sesión ha caducado');
            }
            const negocio = refresco[1] ?? 'sin-negocio';
            return json(200, tokens(negocio === 'sin-negocio' ? null : negocio));
        }
        if (metodo === 'POST' && ruta === '/v1/auth/email-verifications/confirm') {
            return cuerpo.token === 'caducado' ? problema(422, 'code_invalid', 'Este enlace ha caducado o ya se ha usado.') : new Response(null, { status: 204 });
        }
        if (metodo === 'POST' && ruta === '/v1/auth/password-resets') {
            return new Response(null, { status: 202 });
        }
        if (metodo === 'POST' && ruta === '/v1/auth/password-resets/confirm') {
            const nueva = typeof cuerpo.new_password === 'string' ? cuerpo.new_password : '';
            if (nueva.length < 12) {
                return problema(422, 'validation_failed', 'Datos no válidos', { errors: [{ field: 'new_password', code: 'too_short', message: 'Mínimo 12 caracteres.' }] });
            }
            return new Response(null, { status: 204 });
        }

        // A partir de aquí, todo pide sesión.
        if (token === null) {
            return problema(401, 'unauthenticated', 'Inicia sesión');
        }
        if (metodo === 'POST' && ruta === '/v1/auth/email-verifications') {
            return new Response(null, { status: 202 });
        }
        if (metodo === 'DELETE' && ruta === '/v1/auth/sessions/current') {
            return new Response(null, { status: 204 });
        }
        if (metodo === 'POST' && ruta === '/v1/auth/sessions/current/business') {
            const id = typeof cuerpo.business_id === 'string' ? cuerpo.business_id : '';
            if (opciones.soporteSinDosPasos === true) {
                return problema(403, 'two_factor_required', 'Tu cuenta necesita la verificación en dos pasos');
            }
            if (!negociosValidos.includes(id)) {
                return problema(404, 'not_found', 'Negocio no encontrado');
            }
            return json(200, tokens(id));
        }
        if (metodo === 'GET' && ruta === '/v1/me') {
            return json(200, yo(opciones.unNegocio === true, estado, opciones.sinNegocio === true));
        }
        if (metodo === 'POST' && ruta === '/v1/me/sessions/current/two-factor-confirmation') {
            const codigo = cuerpo.two_factor_code ?? cuerpo.recovery_code;
            if (codigo === CUENTA_DEMO.codigoErroneo) {
                return problema(422, 'code_invalid', 'El código no es correcto');
            }
            const ahoraMismo = ahora();
            return json(200, { confirmed_at: marca(), valid_until: new Date(ahoraMismo.getTime() + 10 * 60_000).toISOString().replace(/\.\d{3}Z$/, 'Z') });
        }
        const cierre = /^\/v1\/platform\/accesses\/(pac_[0-9A-Z]+)\/close$/.exec(ruta);
        if (metodo === 'POST' && cierre !== null) {
            const suplanta = accesoAbierto === 'suplantacion';
            accesoAbierto = null;
            return json(200, {
                id: cierre[1],
                business_id: NEGOCIOS.embrujo,
                mode: suplanta ? 'impersonation' : 'read',
                impersonated_member_id: suplanta ? 'mem_01JA8Z6Q5V3K9R2M7T4N8P1M01' : null,
                impersonated_member_name: suplanta ? 'Uri Ramírez' : null,
                opened_by_name: 'Carlos Vega',
                reason: 'El cliente dice que no le llegan los recordatorios',
                opened_at: marca(),
                expires_at: marca(),
                closed_at: marca(),
                close_reason: 'closed',
            });
        }

        const enNegocio = /^\/v1\/businesses\/(biz_[0-9A-Z]+)(\/.*)?$/.exec(ruta);
        if (enNegocio === null) {
            return problema(404, 'not_found', 'No existe en la simulación');
        }
        const negocio = enNegocio[1] ?? '';
        const resto = enNegocio[2] ?? '';
        if (!negociosValidos.includes(negocio)) {
            return problema(403, 'tenant_mismatch', 'No trabajas en este negocio');
        }
        const suEstado = estado(negocio);
        const acceso = accesoEn(negocio);
        // Soporte sin permiso de escritura ve el negocio en solo lectura (G70, G114).
        const soloLectura = modoDe(suEstado) === 'read_only' || (acceso?.viewer === 'platform' && acceso.mode === 'read');
        const guardadas = preferencias.get(negocio) ?? SIN_PREFERENCIAS;

        if (metodo === 'GET' && resto === '/me/navigation') {
            // Con un acceso de plataforma, el orden de siempre (G129).
            return json(200, navegacion(negocio, soloLectura, permisosDelNegocio(negocio), acceso?.viewer === 'platform' ? SIN_PREFERENCIAS : guardadas, acceso));
        }
        if (resto === '/me/navigation-preferences' && (metodo === 'GET' || metodo === 'PATCH')) {
            if (acceso?.viewer === 'platform') {
                return problema(403, 'permission_denied', 'Solo los miembros del negocio personalizan su menú');
            }
            if (metodo === 'GET') {
                return json(200, guardadas);
            }
            const cambios = cuerpo as PreferenciasMenu;
            const campo = preferenciasNoValidas(negocio, cambios);
            if (campo !== null) {
                return problema(422, 'validation_failed', 'Datos no válidos', { errors: [{ field: campo, code: 'invalid', message: 'No se puede guardar así.' }] });
            }
            const nuevas = unirPreferencias(guardadas, cambios, marca());
            preferencias.set(negocio, nuevas);
            return json(200, nuevas);
        }
        if (metodo === 'GET' && resto === '') {
            return json(200, negocioSimulado(negocio));
        }
        const listas = listasPequenas();
        const lista = { '/members': listas.miembros, '/invitations': listas.invitaciones, '/roles': listas.roles, '/devices': listas.tablets, '/invoices': listas.facturas }[resto];
        if (metodo === 'GET' && lista !== undefined) {
            return json(200, lista);
        }
        if (metodo === 'GET' && resto === '/entitlements') {
            return json(200, bloques(negocio, suEstado, ahora(), soloLectura));
        }
        if (metodo === 'GET' && resto === '/subscription') {
            return json(200, suscripcion(negocio, suEstado, ahora()));
        }
        if (metodo === 'GET' && resto === '/usage') {
            return json(200, uso(opciones.cupo === true, opciones.cupo100 === true));
        }
        if (metodo === 'GET' && resto === '/notices/summary') {
            return json(200, resumen(negocio));
        }
        if (metodo === 'GET' && resto === '/notices') {
            const estado = url.searchParams.get('state');
            const gravedad = url.searchParams.get('severity');
            const lista: ListaAvisos = {
                data: avisosDe(negocio).filter((a) => (estado === null ? a.state !== 'archived' : a.state === estado) && (gravedad === null || a.severity === gravedad)),
                next_cursor: null,
            };
            return json(200, lista);
        }
        if (metodo === 'POST' && resto === '/notices/read-all') {
            const lista = avisosDe(negocio);
            const sinLeer = lista.filter((a) => a.state === 'unread').length;
            bandeja.set(negocio, lista.map((a) => (a.state === 'unread' ? { ...a, state: 'read', read_at: marca() } : a)));
            return json(200, { updated: sinLeer });
        }
        if (metodo === 'GET' && resto === '/my-notice-types') {
            return json(200, { data: silencios.get(negocio) ?? tiposDeAviso() });
        }
        const silencio = /^\/notice-mutes\/([a-z0-9_.]+)$/.exec(resto);
        if ((metodo === 'PUT' || metodo === 'DELETE') && silencio !== null) {
            const codigo = silencio[1] ?? '';
            const tipos = silencios.get(negocio) ?? tiposDeAviso();
            const tipo = tipos.find((t) => t.notice_type === codigo);
            if (tipo === undefined) {
                return problema(404, 'not_found', 'Tipo de aviso no encontrado');
            }
            if (metodo === 'PUT' && !tipo.mutable) {
                return problema(409, 'conflict', 'Los avisos críticos no se pueden silenciar.');
            }
            silencios.set(negocio, tipos.map((t) => (t.notice_type === codigo ? { ...t, muted: metodo === 'PUT', muted_at: metodo === 'PUT' ? marca() : null } : t)));
            return metodo === 'PUT' ? json(200, { notice_type: codigo, muted_at: marca() }) : new Response(null, { status: 204 });
        }
        const sobreAviso = /^\/notices\/(ntc_[0-9A-Z]+)\/(read|archive)$/.exec(resto);
        if (metodo === 'POST' && sobreAviso !== null) {
            const id = sobreAviso[1] ?? '';
            return sobreAviso[2] === 'read'
                ? cambiarAviso(negocio, id, (a) => (a.state === 'unread' ? { ...a, state: 'read', read_at: marca() } : a))
                : cambiarAviso(negocio, id, (a) => ({ ...a, state: 'archived', archived_at: marca() }));
        }

        return problema(404, 'not_found', 'No existe en la simulación');
    }

    return async (entrada, init) => {
        const url = new URL(entrada instanceof Request ? entrada.url : String(entrada), 'http://simulacion.local');
        const metodo = init?.method ?? 'GET';
        if ((opciones.espera ?? 0) > 0) {
            await new Promise((resolver) => setTimeout(resolver, opciones.espera));
        }
        return responder(metodo, url, init);
    };
}

/** Estados de `?suscripcion=`, con los nombres del prototipo (0.1). */
const SUSCRIPCIONES: Record<string, EstadoSuscripcion> = {
    prueba: 'trialing',
    activa: 'active',
    impagada: 'past_due',
    suspendida: 'suspended',
    pausada: 'paused',
    baja: 'cancel_scheduled',
    cancelada: 'cancelled',
    caducada: 'trial_expired',
    cerrada: 'closed',
};

/**
 * Opciones de la simulación desde la dirección, para revisar el diseño:
 * `?simular=solo-lectura,un-negocio,sin-negocio,sin-dos-pasos,soporte-sin-dos-pasos,soporte,suplantacion,soporte-en-negocio,cupo,cupo100` y
 * `?suscripcion=prueba|activa|impagada|suspendida|pausada|baja|cancelada|caducada|cerrada` (como el prototipo).
 */
export function opcionesDesdeDireccion(busqueda: string): OpcionesSimulacion {
    const parametros = new URLSearchParams(busqueda);
    const marcas = (parametros.get('simular') ?? '').split(',');
    return {
        soloLectura: marcas.includes('solo-lectura'),
        unNegocio: marcas.includes('un-negocio'),
        sinNegocio: marcas.includes('sin-negocio'),
        sinDosPasos: marcas.includes('sin-dos-pasos'),
        soporteSinDosPasos: marcas.includes('soporte-sin-dos-pasos'),
        suscripcion: SUSCRIPCIONES[parametros.get('suscripcion') ?? ''] ?? null,
        cupo: marcas.includes('cupo'),
        cupo100: marcas.includes('cupo100'),
        acceso: marcas.includes('suplantacion') ? 'suplantacion' : marcas.includes('soporte') ? 'soporte' : marcas.includes('soporte-en-negocio') ? 'negocio' : null,
        espera: 350,
    };
}
